import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FONT, COLOURS, SWIPE_THRESHOLD, TRANSITION_MS } from '../constants/theme';
import StatusBar from '../components/ui/StatusBar';
import Dialog    from '../components/ui/Dialog';

/**
 * EnvelopeScreen — the opening invitation card with a Tinder-style swipe gesture.
 *
 * Guest journey:
 *   - Swipe RIGHT  (or tap the button) → accepted → transitions to DockScene.
 *   - Swipe LEFT                        → rejected → card snaps back.
 *
 * ── Bug fix: global mouse/touch listeners ────────────────────────────────
 * The original implementation only listened for mouse events on the card div
 * itself.  If the user's pointer left the card boundary during a drag, the
 * mouseup event was never received and the drag state became stuck.
 *
 * Fix: when `dragging` is true, attach mousemove/mouseup to `window` via a
 * useEffect, ensuring the drag always resolves regardless of pointer position.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * @param {object}   props
 * @param {function} props.onAccept - Callback fired when the user accepts.
 */
function EnvelopeScreen({ onAccept }) {
  // Current horizontal drag offset in pixels
  const [dx,       setDx]       = useState(0);
  const [dragging, setDragging] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [rejected, setRejected] = useState(false);

  // Refs — updated synchronously during drag without triggering re-renders
  const startX = useRef(0);
  const dxRef  = useRef(0);   // mirrors dx state for use in callbacks

  /**
   * Finish the drag gesture: check threshold and animate accept/reject.
   * Uses dxRef (not dx state) to avoid stale closure issues.
   */
  const finishDrag = useCallback(() => {
    const currentDx = dxRef.current;
    setDragging(false);

    if (currentDx > SWIPE_THRESHOLD) {
      setAccepted(true);
      setTimeout(() => onAccept(), TRANSITION_MS);
    } else if (currentDx < -SWIPE_THRESHOLD) {
      setRejected(true);
      setTimeout(() => {
        setRejected(false);
        setDx(0);
        dxRef.current = 0;
      }, TRANSITION_MS);
    } else {
      // Below threshold — snap back to centre
      setDx(0);
      dxRef.current = 0;
    }
  }, [onAccept]);

  /**
   * Global mouse/touch event attachment.
   * Only active while `dragging` to minimise unnecessary listeners.
   */
  useEffect(() => {
    if (!dragging) return undefined;

    const handleMouseMove = (e) => {
      const delta = e.clientX - startX.current;
      dxRef.current = delta;
      setDx(delta);
    };

    const handleTouchMove = (e) => {
      const delta = e.touches[0].clientX - startX.current;
      dxRef.current = delta;
      setDx(delta);
    };

    const handleUp = () => finishDrag();

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup',   handleUp);
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend',  handleUp);

    // Cleanup — always remove listeners when dragging stops or component unmounts
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup',   handleUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend',  handleUp);
    };
  }, [dragging, finishDrag]);

  /** Start a drag from a pointer position. */
  const startDrag = (clientX) => {
    startX.current = clientX;
    setDragging(true);
  };

  /* ── Derived display values ── */
  const rotation  = dx * 0.08;
  const isGreen   = dx > 40;
  const isRed     = dx < -40;

  const bgColour  = isGreen
    ? 'rgba(180,240,160,0.15)'
    : isRed
      ? 'rgba(240,160,160,0.15)'
      : 'transparent';

  // Fly the card off-screen on accept/reject
  const cardTranslateX = accepted ? 400 : rejected ? -400 : dx;
  const cardRotation   = accepted ? 15  : rejected ? -15  : rotation;
  const cardOpacity    = accepted || rejected ? 0 : 1;

  return (
    <div
      style={{
        fontFamily:     FONT,
        background:     COLOURS.parchmentDark,
        minHeight:      '100vh',
        display:        'flex',
        flexDirection:  'column',
      }}
    >
      <StatusBar left="✿ LOVI & MARKO" mid="YOU HAVE MAIL" right="♥♥♥♥♥" />

      {/* ── Main content area ── */}
      <div
        style={{
          flex:           1,
          display:        'flex',
          flexDirection:  'column',
          alignItems:     'center',
          justifyContent: 'center',
          padding:        '20px 16px',
          position:       'relative',
          overflow:       'hidden',
          background:     bgColour,
          transition:     'background 0.2s',
        }}
      >
        {/* Decorative corner flourishes */}
        <svg
          width="100%"
          viewBox="0 0 380 60"
          style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
          aria-hidden="true"
        >
          {/* Top-left corner */}
          <rect x="8"   y="8"  width="40" height="3"  fill="#c9843c" opacity="0.4" />
          <rect x="8"   y="8"  width="3"  height="40" fill="#c9843c" opacity="0.4" />
          <rect x="12"  y="12" width="3"  height="3"  fill="#c9843c" opacity="0.6" />
          {/* Top-right corner */}
          <rect x="332" y="8"  width="40" height="3"  fill="#c9843c" opacity="0.4" />
          <rect x="369" y="8"  width="3"  height="40" fill="#c9843c" opacity="0.4" />
          <rect x="365" y="12" width="3"  height="3"  fill="#c9843c" opacity="0.6" />
          {/* Scattered decorative maple leaves */}
          <rect x="30"  y="28" width="5"  height="4"  fill="#c84a18" opacity="0.4" />
          <rect x="348" y="32" width="5"  height="4"  fill="#c84a18" opacity="0.4" />
        </svg>

        {/* Swipe direction labels — brighten when threshold approached */}
        <div
          style={{
            display:        'flex',
            justifyContent: 'space-between',
            width:          '100%',
            maxWidth:       300,
            marginBottom:   16,
            position:       'relative',
            zIndex:         5,
          }}
        >
          <div
            style={{
              fontFamily: FONT,
              fontSize:   6,
              color:      isRed ? '#c84a18' : 'rgba(180,100,80,0.35)',
              transition: 'color 0.2s',
              textAlign:  'center',
              lineHeight: 2,
            }}
          >
            ← DECLINE
            <br />
            <span style={{ fontSize: 5 }}>sorry can&apos;t make it</span>
          </div>
          <div
            style={{
              fontFamily: FONT,
              fontSize:   6,
              color:      isGreen ? '#5a8f30' : 'rgba(100,130,80,0.35)',
              transition: 'color 0.2s',
              textAlign:  'center',
              lineHeight: 2,
            }}
          >
            ACCEPT →
            <br />
            <span style={{ fontSize: 5 }}>i&apos;ll be there!</span>
          </div>
        </div>

        {/* ── Draggable envelope card ── */}
        <div
          role="button"
          tabIndex={0}
          aria-label="Wedding invitation — drag right to accept, left to decline"
          onMouseDown={(e) => startDrag(e.clientX)}
          onTouchStart={(e) => startDrag(e.touches[0].clientX)}
          // Keyboard accessibility: Enter / Space also accept
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onAccept(); }}
          style={{
            width:      '100%',
            maxWidth:   300,
            cursor:     dragging ? 'grabbing' : 'grab',
            transform:  `translateX(${cardTranslateX}px) rotate(${cardRotation}deg)`,
            transition: dragging ? 'none' : 'transform 0.4s cubic-bezier(0.34,1.56,0.64,1)',
            opacity:    cardOpacity,
            position:   'relative',
            zIndex:     10,
            userSelect: 'none',
          }}
        >
          <svg
            width="100%"
            viewBox="0 0 300 220"
            style={{ display: 'block', filter: 'drop-shadow(4px 4px 0 rgba(0,0,0,0.15))' }}
            aria-hidden="true"
          >
            {/* Envelope body */}
            <rect x="0"   y="40"  width="300" height="180" fill="#f5e8c8" />
            <rect x="0"   y="40"  width="300" height="180" fill="none" stroke="#c9843c" strokeWidth="4" />

            {/* Back fold */}
            <polygon points="0,40 150,130 300,40"           fill="#ede0b0" />
            <polygon points="0,40 150,130 300,40"           fill="none" stroke="#c9843c" strokeWidth="2" />

            {/* Front flap (open style) */}
            <polygon points="0,40 150,130 300,40 300,0 0,0" fill="#e8d4a0" />
            <polygon points="0,40 150,130 300,40 300,0 0,0" fill="none" stroke="#c9843c" strokeWidth="3" />
            <polygon points="10,0 150,110 290,0"            fill="#f0e4b8" opacity="0.6" />

            {/* Wax seal */}
            <circle cx="150" cy="114" r="22" fill="#b85c38" />
            <circle cx="150" cy="114" r="18" fill="#c86a40" />
            <circle cx="150" cy="114" r="14" fill="#b85c38" />
            <text x="150" y="118" textAnchor="middle" fontFamily={FONT} fontSize="9" fill="#fff8e0">
              L&amp;M
            </text>

            {/* Dashed inner border */}
            <rect x="6" y="46" width="288" height="168" fill="none"
              stroke="#e8c89a" strokeWidth="2" strokeDasharray="6,4" />

            {/* Side folds */}
            <line x1="0"   y1="40" x2="0"   y2="220" stroke="#c9843c" strokeWidth="2" />
            <line x1="300" y1="40" x2="300" y2="220" stroke="#c9843c" strokeWidth="2" />
            <polygon points="0,220 150,140 300,220"         fill="#e8d4a0" />
            <polygon points="0,220 150,140 300,220"         fill="none" stroke="#c9843c" strokeWidth="2" />

            {/* Peeking inner card */}
            <rect x="40"  y="4"  width="220" height="38" fill="#fff8f0" opacity="0.9" />
            <rect x="40"  y="4"  width="220" height="38" fill="none" stroke="#e8c89a" strokeWidth="2" />
            <text x="150" y="16" textAnchor="middle" fontFamily={FONT} fontSize="5" fill="#8a6848">
              ✿ You are invited ✿
            </text>
            <text x="150" y="30" textAnchor="middle" fontFamily={FONT} fontSize="8" fill="#b85c38">
              Sylvie &amp; Marko
            </text>

            {/* Corner pixel hearts (decorative) */}
            {[22, 272].map((hx) => (
              <g key={hx}>
                <rect x={hx}   y="58" width="2" height="2" fill="#c9843c" opacity="0.6" />
                <rect x={hx-2} y="60" width="6" height="2" fill="#c9843c" opacity="0.6" />
                <rect x={hx-1} y="62" width="4" height="2" fill="#c9843c" opacity="0.6" />
                <rect x={hx}   y="64" width="2" height="1" fill="#c9843c" opacity="0.6" />
              </g>
            ))}

            {/* Postage stamp */}
            <rect x="268" y="4" width="28" height="18" fill="#e8c89a" />
            <rect x="270" y="6" width="24" height="14" fill="#c9843c" opacity="0.3" />
            <text x="282" y="16" textAnchor="middle" fontFamily={FONT} fontSize="5" fill="#8a3a18">
              2026
            </text>
          </svg>
        </div>

        {/* Swipe hint text */}
        <div style={{ marginTop: 20, textAlign: 'center', fontFamily: FONT }}>
          <div
            style={{
              fontSize:  7,
              color:     COLOURS.primary,
              marginBottom: 8,
              animation: 'swipeHint 1.8s ease-in-out infinite',
            }}
          >
            SWIPE RIGHT TO ACCEPT ──→
          </div>
          <div style={{ fontSize: 6, color: '#a08060', lineHeight: 2 }}>
            Drag the envelope to accept
            <br />
            your invitation
          </div>
        </div>

        {/* Fallback tap button for non-touch devices */}
        <button
          type="button"
          onClick={onAccept}
          style={{
            marginTop:   16,
            fontFamily:  FONT,
            fontSize:    7,
            color:       COLOURS.textInverted,
            background:  COLOURS.primary,
            border:      `3px solid ${COLOURS.primaryDark}`,
            padding:     '10px 20px',
            cursor:      'pointer',
            boxShadow:   `3px 3px 0 ${COLOURS.primaryDeep}`,
            letterSpacing: 1,
          }}
        >
          ✿ ACCEPT INVITATION ✿
        </button>

        {/* Bottom corner flourishes */}
        <svg
          width="100%"
          viewBox="0 0 380 50"
          style={{ position: 'absolute', bottom: 0, left: 0, pointerEvents: 'none' }}
          aria-hidden="true"
        >
          <rect x="8"   y="2" width="40" height="3"  fill="#c9843c" opacity="0.4" />
          <rect x="8"   y="2" width="3"  height="40" fill="#c9843c" opacity="0.4" />
          <rect x="332" y="2" width="40" height="3"  fill="#c9843c" opacity="0.4" />
          <rect x="369" y="2" width="3"  height="40" fill="#c9843c" opacity="0.4" />
        </svg>
      </div>

      {/* Matcha messenger dialog */}
      <div
        style={{
          padding:     '10px 12px',
          borderTop:   `3px solid ${COLOURS.borderGold}`,
          background:  COLOURS.parchment,
        }}
      >
        <Dialog name="🐾 MATCHA [MESSENGER]">
          &quot;I carried this all the way from the ryokan. My paws are tiny but
          my dedication is enormous.&quot;
          <span className="cursor">█</span>
        </Dialog>
      </div>
    </div>
  );
}

EnvelopeScreen.propTypes = {
  onAccept: PropTypes.func.isRequired,
};

export default EnvelopeScreen;
